/**
 * Provider Service
 * Handles provider-related API calls
 */

import api from '../api';
import { PROVIDERS, getEnabledProviders } from '../../utils/providers/providerConfig';

class ProviderService {
  /**
   * Get list of available providers
   * @returns {Promise<Array>}
   */
  async getProviders() {
    try {
      // For now, return enabled providers from config
      // In future, can fetch from backend
      const enabledProviders = getEnabledProviders();
      return enabledProviders;
    } catch (error) {
      console.error('Error fetching providers:', error);
      // Fallback to enabled providers from config
      return getEnabledProviders();
    }
  }

  /**
   * Get provider configuration
   * @param {string} providerId - Provider ID
   * @returns {Promise<object>}
   */
  async getProvider(providerId) {
    try {
      const provider = PROVIDERS[providerId];
      if (!provider) {
        throw new Error(`Provider not found: ${providerId}`);
      }
      return provider;
    } catch (error) {
      console.error('Error fetching provider:', error);
      throw error;
    }
  }

  /**
   * Get resource types for a provider
   * @param {string} providerId - Provider ID
   * @returns {Promise<Array>}
   */
  async getResourceTypes(providerId) {
    try {
      const provider = PROVIDERS[providerId];
      if (!provider) {
        return [];
      }

      // Return supported resource types with provider-specific details
      const { getSupportedTypes } = await import('../../utils/providers/resourceTypes');
      return getSupportedTypes(providerId);
    } catch (error) {
      console.error('Error fetching resource types:', error);
      return [];
    }
  }

  /**
   * Check if provider is available
   * @param {string} providerId - Provider ID
   * @returns {boolean}
   */
  isProviderAvailable(providerId) {
    const provider = PROVIDERS[providerId];
    return provider && provider.enabled;
  }

  /**
   * Get default provider
   * @returns {string}
   */
  getDefaultProvider() {
    return 'digitalocean';
  }
}

export default new ProviderService();

